import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AskAiButton from '../aiChat/AskAiButton';
import JsonPreviewTable from './JsonPreviewTable';
import { buildTestFailurePrompt } from '../../services/ai/failurePrompts';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { Binary, Eye, FileText, Copy, Check } from 'lucide-react';
import { Ghost } from './ghosts';
import CodeEditor from './CodeEditor';
import CompactSelectPicker from './CompactSelectPicker';
import { usePreferencesStore } from '../../store/preferencesStore';
import {
  getResponseBodyEditorContent,
  getResponseBodyRaw,
  getResponseContentType,
  isBinaryResponse,
  binaryDataUrl,
  responseHexDump,
} from '../../utils/responseBody';

function prefToViewMode(pref) {
  if (pref === 'raw') return 'raw';
  if (pref === 'preview') return 'preview';
  return 'formatted';
}

/**
 * srcDoc inherits the app's own origin as its base URL, so relative URLs in a
 * previewed page (e.g. /logo.png, /login) would resolve against the app and
 * load the app itself. Inject a <base> pointing at the request URL so relative
 * resources resolve against the server that actually returned the HTML.
 */
/**
 * How much to shrink previewed pages.
 *
 * The response pane is a SPLIT — typically half a window wide — so a page laid
 * out for a full viewport renders zoomed-in relative to the space it has, and
 * a desktop layout wraps into a column. 0.9 buys back roughly a tenth of the
 * width without making body text small enough to squint at. Tunable in one
 * place on purpose; this is a taste setting, not a constant of nature.
 */
const PREVIEW_ZOOM = 0.9;

function injectPreviewBase(html, baseUrl) {
  if (typeof html !== 'string') return html;
  // `zoom` rather than `transform: scale()`: transform would leave the page
  // laid out at the old width and merely paint it smaller, so the horizontal
  // scrollbar would stay. zoom re-lays-out at the new scale, which is the
  // point. Injected LAST so a page's own stylesheet cannot outrank it.
  const zoomTag = `<style>html{zoom:${PREVIEW_ZOOM}}</style>`;
  const baseTag =
    baseUrl && !/<base\b/i.test(html)
      ? `<base href="${String(baseUrl).replace(/"/g, '&quot;')}">`
      : '';
  const inject = `${baseTag}${zoomTag}`;
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => `${m}${inject}`);
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => `${m}<head>${inject}</head>`);
  return `${inject}${html}`;
}

/**
 * True when the previewed HTML was served from the app's own origin. Live-
 * rendering such a page would resolve its relative scripts (e.g.
 * /static/js/bundle.js) against the app and re-initialize the whole app inside
 * the iframe, so callers fall back to the formatted view instead.
 */
function isAppOriginResponse(responseUrl) {
  if (!responseUrl || typeof window === 'undefined') return false;
  try {
    return new URL(responseUrl, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * The sandbox tokens for the HTML preview frame.
 *
 * `allow-same-origin` USED TO BE HERE, alongside `allow-scripts`. Those two
 * together are the documented way to un-sandbox a frame: the content runs
 * script AND is assigned the embedder's origin rather than an opaque one, so a
 * `<script>` in any HTTP response body the user previewed became same-origin
 * code in the app — free to read the app's localStorage, walk `parent`'s DOM,
 * and call `parent.__TAURI_INTERNALS__.invoke`. srcDoc content is attacker
 * data: every server the user sends a request to could reach this.
 *
 * Dropping `allow-same-origin` collapses the frame to an opaque origin, which
 * is what actually enforces the boundary — storage access throws, and `parent`
 * is cross-origin so no property on it is reachable. `allow-scripts` stays so
 * pages that build themselves with inline JS still preview; that script now
 * runs with no more authority than a random web page in a null origin.
 *
 * Same shape as the extension guest frames (see ExtensionHost's
 * `sandbox="allow-scripts"`), which is the pattern this file should have
 * followed from the start. Deliberately still absent: `allow-top-navigation`,
 * `allow-popups`, `allow-modals`, `allow-forms`, `allow-downloads`.
 */
const PREVIEW_SANDBOX = 'allow-scripts';

/** Tab definitions for the response panel (shared with RequestBuilder header). */
export function getResponseTabs(response, testResults = null) {
  if (!response) return [];
  const setCookieHeader = response.headers?.['set-cookie'] || response.headers?.['Set-Cookie'];
  const cookieCount = setCookieHeader ? (Array.isArray(setCookieHeader) ? setCookieHeader.length : 1) : 0;

  const testCount = testResults !== null ? testResults.length : null;

  return [
    { key: 'body',         label: i18n.t('response.body'),        count: null },
    { key: 'headers',      label: i18n.t('response.headers'),     count: response.headers ? Object.keys(response.headers).length : 0 },
    { key: 'cookies',      label: i18n.t('response.cookies'),     count: cookieCount },
    { key: 'test-results', label: i18n.t('response.testResults'), count: testCount },
  ];
}

// ── JSON preview ─────────────────────────────────────────────────────────────
// Moved to ui/JsonPreviewTable.js when it learned to drill into nested objects
// (19.08.2026): a tree with its own expansion state does not belong in a file
// that already owns four tabs, three view modes and a sandboxed HTML frame.

// ── Main component ────────────────────────────────────────────────────────────

const FORMAT_OPTIONS = [
  { value: 'auto',       label: 'Auto'       },
  { value: 'json',       label: 'JSON'       },
  { value: 'xml',        label: 'XML'        },
  { value: 'html',       label: 'HTML'       },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'css',        label: 'CSS'        },
  { value: 'yaml',       label: 'YAML'       },
  { value: 'text',       label: 'Text'       },
];

/**
 * Failed-request state (status 0 — connection refused, DNS failure, invalid URL,
 * etc.). Mirrors the "Awaiting response" loader layout: a centered Apiboo
 * mascot (red, idle — looking around but in place), the error message below it,
 * and a Retry action styled like the loader's Cancel button.
 */
function ResponseError({ message, onRetry }) {
  return (
    <div className="rv-error" role="alert">
      <Ghost mono="#ef4444" size={34} animated mode="idle" style={{ marginBottom: 12 }} />
      <div className="rv-error__title">Request failed</div>
      <div className="rv-error__text">{message || 'The request could not be completed.'}</div>
      {onRetry && (
        <div
          role="button"
          tabIndex={0}
          className="req-loading-cancel"
          onClick={onRetry}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onRetry();
            }
          }}
        >
          Retry
        </div>
      )}
    </div>
  );
}

const ResponseViewer = ({
  response,
  selectedEnvironment,
  globalVariables,
  activeTab = 'body',
  splitLayoutTick = 0,
  loading = false,
  loadingContent = null,
  testResults = null,
  testScriptErrors = null,
  hasTestScript = false,
  onRetry = null,
}) => {
  const { t } = useTranslation();
  const defaultResponseView = usePreferencesStore((s) => s.defaultResponseView);
  // Privacy gate for the "Explain failures" prompt (services/ai/responseAccess.js).
  const aiReadResponses = usePreferencesStore((s) => s.aiReadResponses === true);
  const [viewMode, setViewMode]               = useState(() => prefToViewMode(defaultResponseView));
  const [overrideFormat, setOverrideFormat]   = useState('auto');
  const [copied, setCopied]                   = useState(false);

  // Reset view options when a new response arrives
  useEffect(() => {
    // A PDF or image has no useful formatted view — the alternative is a code
    // editor full of replacement characters — so those OPEN in preview
    // regardless of the saved default. Everything else still honours it.
    const type = response ? getResponseContentType(response) : 'text';
    const previewable = (type === 'pdf' || type === 'image') && isBinaryResponse(response);
    setViewMode(previewable ? 'preview' : prefToViewMode(defaultResponseView));
    setOverrideFormat('auto');
  }, [response?.status, response?.time, defaultResponseView, response]);

  // Formatting a 30 MB JSON body takes a second or more: do it once per body,
  // not on every render (tab switch, split drag, copy-state toggle).
  const bodyData = response?.data;
  const bodyBase64 = response?.data_base64;
  const bodyContentType = response?.headers?.['content-type'];
  const formattedBody = useMemo(
    () => (response ? getResponseBodyEditorContent(response) : { text: '', language: 'text' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the body, not the object
    [bodyData, bodyBase64, bodyContentType]
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(formattedBody.text ?? '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }, [formattedBody]);

  // Memoised: this was recomputed on EVERY render, rebuilding up to 512 KB of
  // string and handing Monaco a new value each time. That model churn is what
  // widened the window for the "Canceled" error on tab switch — Monaco
  // disposing a model while tokenisation is still in flight.
  const hexText = useMemo(
    () => (viewMode === 'hex' ? responseHexDump(response) : ''),
    [viewMode, response]
  );

  if (!response) return null;

  // status 0 = the request never completed (DNS/connection failure, invalid URL).
  const isErrorResponse = response.status === 0;
  const detectedType  = getResponseContentType(response);
  const effectiveType = overrideFormat === 'auto' ? detectedType : overrideFormat;

  const togglePreview = () => setViewMode((m) => (m === 'preview' ? 'formatted' : 'preview'));

  // ── Body toolbar ───────────────────────────────────────────────────────────
  const renderBodyToolbar = () => (
    <div className="rv-body-toolbar">
      <CompactSelectPicker
        value={overrideFormat}
        onChange={setOverrideFormat}
        options={FORMAT_OPTIONS}
        className="rv-format-picker"
        aria-label="Response format"
      />
      {/* HEX sits LEFT of Preview and only for a body that actually HAS bytes
          — `data_base64`. Offering it for a text response would dump the
          from_utf8_lossy string's bytes, i.e. the corruption rather than the
          file. */}
      {isBinaryResponse(response) && (
        <button
          type="button"
          className={`rv-preview-btn${viewMode === 'hex' ? ' rv-preview-btn--active' : ''}`}
          onClick={() => setViewMode((m) => (m === 'hex' ? 'preview' : 'hex'))}
          title="Hex view"
        >
          <Binary size={13} aria-hidden />
          Hex
        </button>
      )}
      <button
        type="button"
        className={`rv-preview-btn${viewMode === 'preview' ? ' rv-preview-btn--active' : ''}`}
        onClick={togglePreview}
        title="Preview"
      >
        <Eye size={13} aria-hidden />
        Preview
      </button>
      <div className="flex-1" />
      <button
        type="button"
        className="cursor-pointer rounded bg-transparent p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
        onClick={handleCopy}
        title="Copy response body"
        aria-label="Copy response body"
      >
        {copied
          ? <Check size={16} className="text-green-500 dark:text-green-400" />
          : <Copy size={16} />
        }
      </button>
    </div>
  );

  // ── Formatted (syntax-highlighted + beautified) ────────────────────────────
  const renderFormattedContent = () => {
    const { text, language: autoLang } = formattedBody;
    const language = overrideFormat === 'auto' ? autoLang : effectiveType;
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col" style={{ minHeight: 0 }}>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" style={{ minHeight: 0 }}>
          <CodeEditor
            splitLayoutTick={splitLayoutTick}
            value={text || ''}
            onChange={() => {}}
            language={language}
            height="100%"
            readOnly={true}
            showLineNumbers={true}
            selectedEnvironment={selectedEnvironment}
            globalVariables={globalVariables}
            placeholder={t('response.responsePlaceholder')}
            hideChrome
          />
        </div>
      </div>
    );
  };

  // ── Raw (unformatted) ──────────────────────────────────────────────────────
  const renderRawContent = () => {
    const { text, language: autoLang } = getResponseBodyRaw(response);
    const language = overrideFormat === 'auto' ? autoLang : effectiveType;
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col" style={{ minHeight: 0 }}>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" style={{ minHeight: 0 }}>
          <CodeEditor
            splitLayoutTick={splitLayoutTick}
            value={text || ''}
            onChange={() => {}}
            language={language}
            height="100%"
            readOnly={true}
            showLineNumbers={false}
            selectedEnvironment={selectedEnvironment}
            globalVariables={globalVariables}
            placeholder={t('response.responsePlaceholder')}
            hideChrome
          />
        </div>
      </div>
    );
  };

  // ── Hex ────────────────────────────────────────────────────────────────────
  const renderHexContent = () => (
    <div className="flex h-full min-h-0 min-w-0 flex-col" style={{ minHeight: 0 }}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" style={{ minHeight: 0 }}>
        <CodeEditor
          splitLayoutTick={splitLayoutTick}
          value={hexText}
          onChange={() => {}}
          language="apiboo-hex"
          height="100%"
          readOnly
          // Line numbers ON: a hex dump is navigated by row as well as by
          // offset, and "line 40" is how you tell someone where to look.
          showLineNumbers
          hideChrome
        />
      </div>
    </div>
  );

  // ── Preview ────────────────────────────────────────────────────────────────
  const renderPreview = () => {
    // PDF → the webview's own viewer, from a data: URL built out of the bytes
    // Rust preserved. Before `data_base64` existed the body reached here as
    // from_utf8_lossy mojibake, which is what "cannot preview pdf" meant.
    if (effectiveType === 'pdf' && isBinaryResponse(response)) {
      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <iframe
            src={binaryDataUrl(response)}
            className="w-full flex-1 border-0 bg-white"
            title="PDF preview"
            style={{ minHeight: 0, border: 'none' }}
          />
        </div>
      );
    }

    // Images — same reasoning, same bytes.
    if (effectiveType === 'image' && isBinaryResponse(response)) {
      return (
        <div className="flex h-full min-h-0 items-center justify-center overflow-auto p-4">
          <img
            src={binaryDataUrl(response)}
            alt="Response preview"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </div>
      );
    }

    // HTML → sandboxed iframe.
    // Never live-render the app's own HTML — it would re-init the app in the iframe.
    if (effectiveType === 'html' && typeof response.data === 'string' && !isAppOriginResponse(response.url)) {
      // No onLoad link-interception handler any more: it reached into
      // `contentDocument` to open http(s) links in the system browser, and that
      // is only possible from a same-origin frame. It cannot be reinstated by
      // injecting a postMessage shim into the previewed page either — the page
      // is untrusted, so it could forge those messages and turn the handler
      // into "hostile response asks the app to shell-open an arbitrary URL".
      // Clicks now navigate inside the sandboxed frame, which is contained:
      // top-level navigation is not granted, and `isAppOriginResponse` already
      // refuses to preview the app's own origin.
      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <iframe
            srcDoc={injectPreviewBase(response.data, response.url)}
            className="w-full flex-1 border-0 bg-white"
            sandbox={PREVIEW_SANDBOX}
            title="HTML Preview"
            style={{ minHeight: 0, border: 'none' }}
          />
        </div>
      );
    }

    // JSON → table
    if (effectiveType === 'json') {
      let parsed = null;
      try {
        const raw = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
        parsed = JSON.parse(raw);
      } catch {
        // fall through to formatted
      }
      if (parsed !== null) {
        return (
          <div className="rv-json-preview-wrap panel-scroll">
            <JsonPreviewTable data={parsed} />
          </div>
        );
      }
    }

    // Fallback — formatted code view
    return renderFormattedContent();
  };

  // ── Headers tab ────────────────────────────────────────────────────────────
  const renderHeadersTab = () => {
    if (!response.headers) {
      return <div className="rv-no-data">{t('response.noHeaders')}</div>;
    }

    const headerEntries = Object.entries(response.headers);
    const headerBytes = Object.keys(response.headers).reduce(
      (total, key) => total + key.length + String(response.headers[key]).length,
      0
    );

    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
        <div className="rv-headers-summary">
          {t('response.headersCount', { count: headerEntries.length, bytes: headerBytes })}
        </div>
        <div className="rv-json-preview-wrap panel-scroll" style={{ flex: 1, minHeight: 0 }}>
          <div className="rv-json-table-wrap">
            <table className="rv-json-table">
              <thead>
                <tr>
                  <th className="rv-json-th">{t('request.key')}</th>
                  <th className="rv-json-th">{t('request.value')}</th>
                </tr>
              </thead>
              <tbody>
                {headerEntries.map(([key, value], index) => (
                  <tr key={`${key}-${index}`} className="rv-json-tr">
                    <td className="rv-json-td rv-json-td--key">{key}</td>
                    <td className="rv-json-td">{String(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ── Cookies tab ────────────────────────────────────────────────────────────
  const renderCookiesTab = () => {
    const setCookieHeader = response.headers?.['set-cookie'] || response.headers?.['Set-Cookie'];
    if (!setCookieHeader) {
      return <div className="rv-headers-summary">{t('response.noCookies')}</div>;
    }

    const cookieStrings = Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader];
    const cookies = cookieStrings.map((cookieStr, index) => {
      const parts = cookieStr.split(';').map((p) => p.trim());
      const [nameValue] = parts;
      const [name, value] = nameValue.split('=');
      const attributes = {};
      parts.slice(1).forEach((part) => {
        const [key, val] = part.split('=');
        attributes[key.toLowerCase()] = val || true;
      });
      return { id: index, name: name?.trim(), value: value?.trim(), domain: attributes.domain, path: attributes.path || '/', expires: attributes.expires, maxAge: attributes['max-age'], httpOnly: attributes.httponly, secure: attributes.secure, sameSite: attributes.samesite };
    });

    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
        <div className="rv-headers-summary">
          {t('response.cookiesCount', { count: cookies.length })}
        </div>
        <div className="rv-json-preview-wrap panel-scroll" style={{ flex: 1, minHeight: 0 }}>
          <div className="rv-json-table-wrap">
            <table className="rv-json-table">
              <thead>
                <tr>
                  <th className="rv-json-th">Name</th>
                  <th className="rv-json-th">Value</th>
                  <th className="rv-json-th">Path</th>
                  <th className="rv-json-th">Expires</th>
                  <th className="rv-json-th">Flags</th>
                </tr>
              </thead>
              <tbody>
                {cookies.map((cookie) => (
                  <tr key={cookie.id} className="rv-json-tr">
                    <td className="rv-json-td rv-json-td--key">{cookie.name}</td>
                    <td className="rv-json-td rv-cookie-value">{cookie.value}</td>
                    <td className="rv-json-td">{cookie.path}</td>
                    <td className="rv-json-td">{cookie.expires || cookie.maxAge ? (cookie.expires || `${cookie.maxAge}s`) : '—'}</td>
                    <td className="rv-json-td rv-cookie-flags">
                      {cookie.httpOnly && <span className="rv-cookie-flag rv-cookie-flag--httponly">HttpOnly</span>}
                      {cookie.secure   && <span className="rv-cookie-flag rv-cookie-flag--secure">Secure</span>}
                      {cookie.sameSite && <span className="rv-cookie-flag rv-cookie-flag--samesite">SameSite={cookie.sameSite}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ── Test results tab ───────────────────────────────────────────────────────
  const renderTestResultsTab = () => {
    // No test script defined
    if (!hasTestScript) {
      return (
        <div className="test-results-empty">
          <FileText size={40} style={{ opacity: 0.3 }} aria-hidden />
          <p className="test-results-empty__title">{t('response.testResultsNoScript')}</p>
        </div>
      );
    }

    // Test script exists but request hasn't been sent yet
    if (testResults === null && !testScriptErrors?.length) {
      return (
        <div className="test-results-empty">
          <FileText size={40} style={{ opacity: 0.3 }} aria-hidden />
          <p className="test-results-empty__title">{t('response.testResultsNotRun')}</p>
        </div>
      );
    }

    const tests = testResults ?? [];
    const passed = tests.filter((r) => r.passed).length;
    const total = tests.length;
    const allPassed = total > 0 && passed === total;
    const anyFailed = total > 0 && passed < total;

    return (
      <div className="test-results-panel">
        {/* Summary bar */}
        {(total > 0 || testScriptErrors?.length > 0) && (
          <div className="test-results-summary-bar">
            <span>{t('response.testResultsSummary', { passed, total })}</span>
            {total > 0 && (
              <span
                className={`test-results-summary-badge ${
                  allPassed
                    ? 'test-results-summary-badge--pass'
                    : anyFailed
                      ? 'test-results-summary-badge--fail'
                      : 'test-results-summary-badge--neutral'
                }`}
              >
                {allPassed ? t('response.testResultsAllPass', { defaultValue: 'All passed' }) : `${total - passed} failed`}
              </span>
            )}
            {/* Only when something failed — a green run needs no explaining. */}
            {anyFailed && (
              <AskAiButton
                label="Explain failures"
                title="Ask the AI assistant why these tests failed"
                prompt={buildTestFailurePrompt({ results: tests, allowBody: aiReadResponses })}
              />
            )}
          </div>
        )}

        {/* Script-level errors (syntax / runtime, not boo.test failures) */}
        {testScriptErrors?.length > 0 && testScriptErrors.map((err, i) => (
          <div key={i} className="test-results-script-error">
            {t('response.testResultsScriptError', { message: err })}
          </div>
        ))}

        {/* No tests were registered via boo.test() */}
        {tests.length === 0 && !testScriptErrors?.length && (
          <div className="test-results-empty" style={{ flex: 1 }}>
            <FileText size={40} style={{ opacity: 0.3 }} aria-hidden />
            <p className="test-results-empty__title">{t('response.testResultsHint')}</p>
          </div>
        )}

        {/* Test result list */}
        {tests.length > 0 && (
          <div className="test-results-list" role="list" aria-label={t('response.testResults')}>
            {tests.map((result, i) => (
              <div
                key={i}
                className={`test-result-item ${result.passed ? 'test-result-item--pass' : 'test-result-item--fail'}`}
                role="listitem"
              >
                <span className="test-result-item__icon" aria-hidden>
                  {result.passed ? '✓' : '✗'}
                </span>
                <div className="test-result-item__body">
                  <div className="test-result-item__name">{result.name}</div>
                  {!result.passed && result.error && (
                    <div className="test-result-item__error">{result.error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full min-w-0 w-full flex-col" style={{ minHeight: 0 }}>
      <div className="min-w-0 w-full flex-1 overflow-hidden" style={{ minHeight: 0 }}>

        {activeTab === 'body' && (
          <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden" style={{ minHeight: 0 }}>
            {!isErrorResponse && renderBodyToolbar()}
            {/*
              `relative` wrapper so the loading overlay is scoped to the editor area only —
              the toolbar row above stays fully visible and interactive while loading.
            */}
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" style={{ minHeight: 0 }}>
              {loading && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white dark:bg-[#161618]" style={{ gap: 4 }}>
                  {loadingContent}
                </div>
              )}
              {isErrorResponse ? (
                // While retrying, the stale error stays in `response` (only
                // `loading` flips). Don't paint it — the loader overlay owns the
                // view — or the two sets of text overlap.
                loading ? null : (
                  <ResponseError
                    message={typeof response.data === 'string' ? response.data : null}
                    onRetry={onRetry}
                  />
                )
              ) : (
                <>
                  {viewMode === 'preview'   && renderPreview()}
                  {viewMode === 'formatted' && renderFormattedContent()}
                  {viewMode === 'raw'       && renderRawContent()}
                  {viewMode === 'hex'       && renderHexContent()}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'headers'      && renderHeadersTab()}
        {activeTab === 'cookies'      && renderCookiesTab()}
        {activeTab === 'test-results' && renderTestResultsTab()}
      </div>
    </div>
  );
};

export default ResponseViewer;
